// ** Components
import Footer from './components/shared-components/footer';
import AppBar from './components/vertical/appBar';
import Navigation from './components/vertical/navigation';
import Background from './components/Background';

// ** Theme Config Import
import themeConfig from '../config';

// ** Type Import
import type { LayoutProps } from '../types';
import Box, { type BoxProps } from '@mui/material/Box';

// ** MUI Imports
import { styled } from '@mui/material/styles';

const VerticalLayoutWrapper = styled('div')({
  height: '100%',
  minHeight: '100vh',
  display: 'flex',
});

const MainContentWrapper = styled(Box)<BoxProps>({
  flexGrow: 1,
  minWidth: 0,
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
});

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  padding: theme.mixins.contentPadding(1),
  transition: 'padding .25s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.mixins.contentPadding(0.5),
  },
}));

const VerticalLayout = (props: LayoutProps) => {
  // ** Props
  const {
    settings,
    saveSettings,
    children,
    footerProps,
    contentHeightFixed,
    noPadding,
    verticalLayoutProps,
    contentAlign,
  } = props;

  // ** Vars
  const { skin, navHidden, contentWidth } = settings;
  const { navigationSize, collapsedNavigationSize } = themeConfig.config;
  const navWidth = navigationSize;
  const navigationBorderWidth = skin === 'bordered' ? 1 : 0;
  const collapsedNavWidth = collapsedNavigationSize;

  const navVisible = settings.navVisible ?? false;

  const setNavVisible = (value: boolean): void => {
    saveSettings({ ...settings, navVisible: value });
  };

  const toggleNavVisibility = (): void => {
    setNavVisible(!navVisible);
  };

  return (
    <>
      <VerticalLayoutWrapper className="layout-wrapper">
        <Background />
        {/* Navigation Menu */}
        {navHidden && !(navHidden && settings.lastLayout === 'horizontal') ? null : (
          <Navigation
            navWidth={navWidth}
            navVisible={navVisible}
            setNavVisible={setNavVisible}
            collapsedNavWidth={collapsedNavWidth}
            toggleNavVisibility={toggleNavVisibility}
            navigationBorderWidth={navigationBorderWidth}
            navMenuContent={verticalLayoutProps.navMenu.content}
            navMenuBranding={verticalLayoutProps.navMenu.branding}
            verticalNavItems={verticalLayoutProps.navMenu.navItems}
            navMenuProps={verticalLayoutProps.navMenu.componentProps}
            afterNavMenuContent={verticalLayoutProps.navMenu.afterContent}
            beforeNavMenuContent={verticalLayoutProps.navMenu.beforeContent}
            {...props}
          />
        )}
        <MainContentWrapper
          className="layout-content-wrapper"
          sx={{ ...(contentHeightFixed && { maxHeight: '100vh' }) }}
        >
          {/* AppBar Component */}
          <AppBar
            toggleNavVisibility={toggleNavVisibility}
            appBarContent={verticalLayoutProps.appBar?.content}
            appBarProps={verticalLayoutProps.appBar?.componentProps}
            {...props}
          />

          {/* Content */}
          <ContentWrapper
            className="layout-page-content"
            sx={{
              ...(contentHeightFixed
                ? {
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }
                : { overflowX: 'hidden' }),
              ...(noPadding && { padding: 0 }),
              ...(contentAlign && { alignItems: contentAlign, '& > div': { maxWidth: '100%' } }),
              ...(contentWidth === 'boxed' && {
                mx: 'auto',
                '@media (min-width:1440px)': { maxWidth: 1440 },
                '@media (min-width:1200px)': { maxWidth: '100%' },
              }),
            }}
          >
            {children}
          </ContentWrapper>

          {/* Footer Component */}
          <Footer footerStyles={footerProps?.sx} footerContent={footerProps?.content} {...props} />
        </MainContentWrapper>
      </VerticalLayoutWrapper>
    </>
  );
};

export default VerticalLayout;
